import { useEffect, useMemo, useState } from 'react';
import { ContactInfo } from '../../Utils/Data';
import { useLanguage } from '../../i18n/LanguageContext';
import { useLocalizedStatic } from '../../i18n/useLocalizedStatic';

const WHATSAPP_COPY = {
  welcome: 'Welcome to Q-Tech Contracting',
  prompt: 'Have a question? Ask our team directly on WhatsApp.',
  message: 'Hello, I would like to ask about Q-Tech Contracting services.',
  send: 'Send message',
  header: 'Q-Tech WhatsApp',
  aria: 'Open WhatsApp chat',
  close: 'Close',
};

const playOpenSound = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const ctx = new AudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => playTone(ctx)).catch(() => {});
      return;
    }
    playTone(ctx);
  } catch {
    // ignore autoplay restrictions
  }
};

const playTone = (ctx) => {
  const tone = (delay, frequency, volume, duration, type = 'sine') => {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    const start = ctx.currentTime + delay;
    const end = start + duration;

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    oscillator.start(start);
    oscillator.stop(end);
  };

  tone(0, 740, 0.16, 0.16, 'sine');
  tone(0.18, 988, 0.22, 0.26, 'triangle');
};

function WhatsAppChat() {
  const { locale } = useLanguage();
  const copy = useLocalizedStatic(WHATSAPP_COPY);
  const isRtl = locale === 'ar';
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [badge, setBadge] = useState(0);

  const whatsappUrl = useMemo(() => {
    const phone = ContactInfo.contact_phone.replace(/\D/g, '');
    return `https://wa.me/${phone}?text=${encodeURIComponent(copy.message)}`;
  }, [copy.message]);

  useEffect(() => {
    if (open) setBadge(0);
  }, [open]);

  useEffect(() => {
    let audioReady = false;
    const unlockAudio = async () => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        if (ctx.state === 'suspended') await ctx.resume();
        audioReady = true;
      } catch {
        // ignore
      }
    };

    const events = ['pointerdown', 'mousedown', 'touchstart', 'keydown'];
    events.forEach((event) => window.addEventListener(event, unlockAudio, { passive: true }));

    const firstTimer = window.setTimeout(() => {
      setStep(1);
      setBadge(1);
      if (audioReady) playOpenSound();
    }, 6000);

    const secondTimer = window.setTimeout(() => {
      setStep(2);
      setBadge(2);
      if (audioReady) playOpenSound();
    }, 9500);

    return () => {
      window.clearTimeout(firstTimer);
      window.clearTimeout(secondTimer);
      events.forEach((event) => window.removeEventListener(event, unlockAudio));
    };
  }, []);

  return (
    <div className={`joinchat joinchat--right${isRtl ? ' joinchat--rtl' : ''}`} aria-hidden="false">
      {open && step > 0 ? (
        <div className="joinchat__panel">
          <div className="joinchat__panel-header">
            <strong>{copy.header}</strong>
            <button
              type="button"
              className="joinchat__close"
              onClick={() => setOpen(false)}
              aria-label={copy.close}
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          </div>
          <div className="joinchat__panel-body">
            {step >= 1 ? (
              <div className="joinchat__message joinchat__message--welcome">{copy.welcome}</div>
            ) : null}
            {step >= 2 ? (
              <div className="joinchat__message joinchat__message--prompt">{copy.prompt}</div>
            ) : null}
          </div>
          <a className="joinchat__send" href={whatsappUrl} target="_blank" rel="noreferrer">
            {copy.send}
          </a>
        </div>
      ) : null}
      <button
        type="button"
        className="joinchat__button"
        aria-label={copy.aria}
        onClick={() => setOpen((value) => !value)}
      >
        <i className="fa fa-whatsapp" aria-hidden="true" />
      </button>
      {badge > 0 ? <div className="joinchat__badge">{badge}</div> : null}
    </div>
  );
}

export default WhatsAppChat;
